import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  Grid,
  IconButton,
  Modal,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from "@mui/material";
import {
  Refresh,
  CheckCircle,
  Cancel,
  Search,
  ReplayCircleFilledOutlined,
  HighlightOffOutlined,
  QrCode2,
  CloudUpload,
  Delete,
  ZoomIn,
  ZoomOut,
  RestartAlt,
  FileDownload
} from "@mui/icons-material";
import { CSVLink } from "react-csv";
import DataTable from "react-data-table-component";
import Page from "src/components/Page";
import Label from "src/components/Label";
import CustomLoader from "src/components/CustomLoader";
import {
  getReferralWithdrawalRequestStatus,
  updateReferralWithdrawalRequestStatus
} from "src/api/commonapi";
import { notification } from "src/utils/messages";
import { Routes } from "../constant/api_url";

export default function ReferralWithdrawalRequests() {
  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 500,
    bgcolor: "background.paper",
    boxShadow: 10,
    borderRadius: "10px",
    p: 4
  };

  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [requests, setRequests] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [perPage, setPerPage] = useState(20);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const searchTimerRef = useRef(null);
  const [statusFilter, setStatusFilter] = useState(-1);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [approveModal, setApproveModal] = useState(false);
  const [rejectModal, setRejectModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [qrModal, setQrModal] = useState(false);
  const [qrRow, setQrRow] = useState(null);
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [paymentScreenshot, setPaymentScreenshot] = useState(null);
  const [screenshotPreview, setScreenshotPreview] = useState("");
  const screenshotInputRef = useRef(null);
  const [screenshotModal, setScreenshotModal] = useState(false);
  const [screenshotViewUrl, setScreenshotViewUrl] = useState("");
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragRef = useRef({ active: false, startX: 0, startY: 0, panX: 0, panY: 0 });

  const statusOptions = [
    { label: "All Status", value: -1 },
    { label: "Pending", value: 0 },
    { label: "Approved", value: 1 },
    { label: "Rejected", value: 2 }
  ];

  const fetchData = useCallback(async () => {
    setLoading(true);
    const resp = await getReferralWithdrawalRequestStatus({
      page,
      per_page: perPage,
      status: statusFilter,
      search: debouncedSearch,
      from_date: fromDate,
      to_date: toDate
    });
    setLoading(false);
    if (resp.status == 1) {
      setRequests(resp.data || []);
      setTotalRecords(resp.total_records || 0);
    } else {
      setRequests([]);
      setTotalRecords(0);
    }
  }, [page, perPage, statusFilter, debouncedSearch, fromDate, toDate]);

  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(searchTimerRef.current);
  }, [search]);

  useEffect(() => {
    fetchData();
  }, [fetchData, page, perPage]);

  const handleFilter = () => {
    setPage(1);
    setDebouncedSearch(search);
  };

  const handleClearFilters = () => {
    setSearch("");
    setDebouncedSearch("");
    setStatusFilter(-1);
    setFromDate("");
    setToDate("");
    setPage(1);
  };

  const exportHeaders = [
    { label: "Member Name", key: "member_name" },
    { label: "Mobile No", key: "mobile_no" },
    { label: "Amount", key: "amount" },
    { label: "Type", key: "payout_type_label" },
    { label: "Requested Date", key: "requested_date" },
    ...(statusFilter == 1 || statusFilter == -1
      ? [{ label: "Approved Date", key: "approved_date" }]
      : []),
    ...(statusFilter == 2 || statusFilter == -1
      ? [{ label: "Rejected Date", key: "rejected_date" }]
      : []),
    { label: "Status", key: "status_label" },
    { label: "UPI ID", key: "upi_id" },
    ...(statusFilter != 2
      ? [{ label: "Payment Screenshot", key: "payment_screenshot" }]
      : []),
    ...(statusFilter == 2 || statusFilter == -1
      ? [{ label: "Rejected Reason", key: "rejected_reason" }]
      : [])
  ];

  const payoutTypeLabel = (type) => {
    return type == 0 ? "UPI" : "Bank";
  };

  const exportData = requests.map((row) => ({
    member_name: row.member_name || "",
    mobile_no: row.mobile_no || "",
    amount: row.amount || 0,
    payout_type_label: payoutTypeLabel(row.payout_type),
    requested_date: row.requested_date || "",
    ...(statusFilter == 1 || statusFilter == -1
      ? { approved_date: row.approved_date || "" }
      : {}),
    ...(statusFilter == 2 || statusFilter == -1
      ? { rejected_date: row.rejected_date || "" }
      : {}),
    status_label: row.status == 0 ? "Pending" : row.status == 1 ? "Approved" : "Rejected",
    upi_id: row.upi_id || "",
    ...(statusFilter != 2
      ? { payment_screenshot: row.payment_screenshot ? `${Routes.API_HOSTNAME}referral-p-screenshot/${row.payment_screenshot}` : "" }
      : {}),
    ...(statusFilter == 2 || statusFilter == -1
      ? { rejected_reason: row.rejected_reason || "" }
      : {})
  }));

  const handleApprove = async () => {
    if (!selectedRow) return;
    if (!paymentScreenshot) {
      const notify = notification({ type: "error", message: "Please upload payment screenshot" });
      notify();
      return;
    }
    setActionLoading(true);
    const formData = new FormData();
    formData.append("wr_amt_r_id", selectedRow.wr_amt_r_id);
    formData.append("m_id", selectedRow.m_id);
    formData.append("status", 1);
    formData.append("payment_screenshot", paymentScreenshot);
    const resp = await updateReferralWithdrawalRequestStatus(formData);
    setActionLoading(false);
    const notify = notification({
      type: resp.status == 0 ? "error" : "success",
      message: resp.message
    });
    notify();
    if (resp.status == 1) {
      setApproveModal(false);
      setSelectedRow(null);
      setPaymentScreenshot(null);
      setScreenshotPreview("");
      fetchData();
    }
  };

  const handleReject = async () => {
    if (!selectedRow || !rejectReason.trim()) {
      const notify = notification({
        type: "error",
        message: "Please enter a reason for rejection"
      });
      notify();
      return;
    }
    setActionLoading(true);
    const resp = await updateReferralWithdrawalRequestStatus({
      wr_amt_r_id: selectedRow.wr_amt_r_id,
      m_id: selectedRow.m_id,
      status: 2,
      rejected_reason: rejectReason.trim()
    });
    setActionLoading(false);
    const notify = notification({
      type: resp.status == 0 ? "error" : "success",
      message: resp.message
    });
    notify();
    if (resp.status == 1) {
      setRejectModal(false);
      setSelectedRow(null);
      setRejectReason("");
      fetchData();
    }
  };

  const openApproveModal = (row) => {
    setSelectedRow(row);
    setPaymentScreenshot(null);
    setScreenshotPreview("");
    setApproveModal(true);
  };

  const openRejectModal = (row) => {
    setSelectedRow(row);
    setRejectReason("");
    setRejectModal(true);
  };

  const openQrModal = async (row) => {
    setQrRow(row);
    setQrModal(true);
    try {
      const QRCode = await import("qrcode");
      const upiUrl = `upi://pay?pa=${row.upi_id}&cu=INR&am=${row.amount}`;
      const dataUrl = await QRCode.toDataURL(upiUrl, { width: 256, margin: 2 });
      setQrDataUrl(dataUrl);
    } catch {
      setQrDataUrl("");
    }
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePerRowsChange = (newPerPage, newPage) => {
    setPerPage(newPerPage);
    setPage(newPage);
  };

  const columns = [
    {
      name: "No",
      selector: (row, index) => (page - 1) * perPage + index + 1,
      width: "70px"
    },
    {
      name: "MEMBER",
      selector: (row) => (
        <div>
          <Typography variant="body2" fontWeight={600}>
            {row.member_name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {row.mobile_no}
          </Typography>
        </div>
      ),
      minWidth: "200px"
    },
    {
      name: "AMOUNT",
      selector: (row) => `${row.amount}`,
      width: "100px",
      center: true
    },
    {
      name: "TYPE",
      selector: (row) => (
        <Label variant="ghost" color="default">
          {payoutTypeLabel(row.payout_type)}
        </Label>
      ),
      width: "90px",
      center: true
    },
    {
      name: "REQUESTED DATE",
      selector: (row) => row.requested_date || "-",
      center: true
    },
    ...(statusFilter == 1 || statusFilter == -1
      ? [{
          name: "APPROVED DATE",
          selector: (row) => row.approved_date || "-",
          center: true
        }]
      : []),
    ...(statusFilter == 2 || statusFilter == -1
      ? [{
          name: "REJECTED DATE",
          selector: (row) => row.rejected_date || "-",
          center: true
        }]
      : []),
    ...(statusFilter != 2
      ? [{
          name: "PAYMENT SCREENSHOT",
          cell: (row) =>
            row.payment_screenshot ? (
              <Box
                component="img"
                src={`${Routes.API_HOSTNAME}referral-p-screenshot/${row.payment_screenshot}`}
                alt="Screenshot"
                onClick={() => {
                  setScreenshotViewUrl(`${Routes.API_HOSTNAME}referral-p-screenshot/${row.payment_screenshot}`);
                  setScreenshotModal(true);
                }}
                sx={{
                  width: 40,
                  height: 40,
                  objectFit: "cover",
                  borderRadius: "8px",
                  border: "1px solid",
                  borderColor: "divider",
                  cursor: "pointer",
                  "&:hover": { opacity: 0.8 }
                }}
              />
            ) : (
              <Typography variant="body2" color="text.secondary">
                -
              </Typography>
            ),
          center: true
        }]
      : []),
    ...(statusFilter == 2 || statusFilter == -1
      ? [{
          name: "REJECTED REASON",
          selector: (row) => row.rejected_reason || "-",
          minWidth: "150px"
        }]
      : []),
    {
      name: "Action",
      wrap: true,
      selector: (row) =>
        row.status == 0 ? (
          <Stack direction="row" spacing={0}>
            {row.payout_type == 0 && row.upi_id && (
              <Tooltip title="View QR Code">
                <IconButton onClick={() => openQrModal(row)}>
                  <QrCode2 color="primary" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Approve Request">
              <IconButton onClick={() => openApproveModal(row)}>
                <ReplayCircleFilledOutlined color="success" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Reject Request">
              <IconButton onClick={() => openRejectModal(row)}>
                <HighlightOffOutlined color="error" />
              </IconButton>
            </Tooltip>
          </Stack>
        ) : row.status == 1 ? (
          <Label variant="ghost" color="success">
            Approved
          </Label>
        ) : (
          <Label variant="ghost" color="error">
            Rejected
          </Label>
        ),
      center: true
    }
  ];

  return (
    <Page title="Referral Withdrawal Requests | Local Vocal Business Group">
      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
          <Typography variant="h4" gutterBottom>
            Referral Withdrawal Requests
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchData}
              disabled={loading}
              sx={{ textTransform: "none" }}
            >
              Refresh Data
            </Button>
            {requests.length > 0 && (
              <Button
                variant="contained"
                disabled={loading}
                startIcon={<FileDownload />}
                sx={{ textTransform: "none" }}
              >
                <CSVLink
                  data={exportData}
                  headers={exportHeaders}
                  filename="Referral Withdrawal Requests.csv"
                  style={{ textDecoration: "none", color: "inherit" }}
                  target="_blank"
                >
                  Export CSV
                </CSVLink>
              </Button>
            )}
          </Stack>
        </Stack>

        <Card sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Search Member"
                placeholder="Search Member..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <Search sx={{ color: "text.secondary", mr: 1, fontSize: 18 }} />
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  {statusOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                size="small"
                label="From Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                size="small"
                label="To Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={1.5}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleFilter}
                disabled={loading}
                sx={{ height: 40, textTransform: "none" }}
              >
                Filter
              </Button>
            </Grid>
            <Grid item xs={6} sm={3} md={1.5}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleClearFilters}
                disabled={loading}
                sx={{ height: 40, textTransform: "none" }}
              >
                Clear
              </Button>
            </Grid>
          </Grid>
        </Card>

        <Card className="p-2" style={{ padding: "15px" }}>
          <DataTable
            filter
            pagination
            paginationServer
            paginationTotalRows={totalRecords}
            onChangePage={handlePageChange}
            onChangeRowsPerPage={handlePerRowsChange}
            highlightOnHover
            columns={columns}
            data={requests}
            progressPending={loading}
            progressComponent={<CustomLoader />}
          />
        </Card>
      </Container>

      <Modal
        open={approveModal}
        onClose={() => setApproveModal(false)}
        slotProps={{ backdrop: { sx: { backdropFilter: "blur(8px)", backgroundColor: "rgba(15, 23, 42, 0.4)" } } }}
      >
        <Box sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 480,
          bgcolor: "background.paper",
          borderRadius: "24px",
          boxShadow: "0 20px 50px rgba(0,0,0,0.2)",
          overflow: "hidden",
          border: "1px solid",
          borderColor: "divider"
        }}>
          <Box sx={{ p: 6, textAlign: "center" }}>
            <Box sx={{ mb: 4, display: "flex", justifyContent: "center" }}>
              <Box sx={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                bgcolor: "rgba(22, 163, 74, 0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "4px solid rgba(22, 163, 74, 0.2)"
              }}>
                <CheckCircle sx={{ color: "success.main", fontSize: 48 }} />
              </Box>
            </Box>
            <Typography variant="h5" fontWeight={700} mb={2}>
              Confirm Approval
            </Typography>
            <Typography variant="body1" color="text.secondary" mb={3} sx={{ lineHeight: 1.8 }}>
              Are you sure you want to approve the withdrawal of{" "}
              <Typography component="span" fontWeight={700} color="text.primary">
                ₹{selectedRow?.amount}
              </Typography>{" "}
              for{" "}
              <Typography component="span" fontWeight={700} color="text.primary">
                {selectedRow?.member_name?.split("(")[0]?.trim() || selectedRow?.member_name}
              </Typography>
              ?
            </Typography>

            <Box sx={{ textAlign: "left", mb: 4 }}>
              <Typography variant="body2" fontWeight={600} color="text.secondary" mb={1}>
                Payment Screenshot <Typography component="span" color="error.main">*</Typography>
              </Typography>
              <input
                type="file"
                accept="image/*"
                ref={screenshotInputRef}
                style={{ display: "none" }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    setPaymentScreenshot(file);
                    setScreenshotPreview(URL.createObjectURL(file));
                  }
                }}
              />
              {screenshotPreview ? (
                <Box sx={{ position: "relative", display: "inline-block" }}>
                  <Box
                    component="img"
                    src={screenshotPreview}
                    alt="Payment Screenshot"
                    sx={{
                      width: "100%",
                      maxHeight: 200,
                      objectFit: "contain",
                      borderRadius: "12px",
                      border: "1px solid",
                      borderColor: "divider",
                      bgcolor: "action.hover"
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => {
                      setPaymentScreenshot(null);
                      setScreenshotPreview("");
                      if (screenshotInputRef.current) screenshotInputRef.current.value = "";
                    }}
                    sx={{
                      position: "absolute",
                      top: 6,
                      right: 6,
                      bgcolor: "error.main",
                      color: "#fff",
                      "&:hover": { bgcolor: "error.dark" }
                    }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              ) : (
                <Box
                  onClick={() => screenshotInputRef.current?.click()}
                  sx={{
                    border: "2px dashed",
                    borderColor: "grey.400",
                    borderRadius: "12px",
                    p: 3,
                    cursor: "pointer",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 1,
                    "&:hover": { borderColor: "primary.main", bgcolor: "action.hover" }
                  }}
                >
                  <CloudUpload sx={{ fontSize: 36, color: "grey.500" }} />
                  <Typography variant="body2" color="text.secondary">
                    Click to upload payment screenshot
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    JPG, PNG up to 5MB
                  </Typography>
                </Box>
              )}
            </Box>

            <Stack direction="row" spacing={2}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  setApproveModal(false);
                  setPaymentScreenshot(null);
                  setScreenshotPreview("");
                  if (screenshotInputRef.current) screenshotInputRef.current.value = "";
                }}
                sx={{ py: 1.5, borderRadius: "12px", textTransform: "none", fontWeight: 600 }}
              >
                Cancel
              </Button>
              <Button
                fullWidth
                variant="contained"
                onClick={handleApprove}
                disabled={actionLoading || !paymentScreenshot}
                startIcon={actionLoading && <CircularProgress size={18} color="inherit" />}
                sx={{
                  py: 1.5,
                  borderRadius: "12px",
                  textTransform: "none",
                  fontWeight: 600,
                  bgcolor: "primary.main",
                  boxShadow: "0 4px 14px rgba(0,40,142,0.2)"
                }}
              >
                Confirm Approve
              </Button>
            </Stack>
          </Box>
          <Box sx={{ height: "6px", background: "linear-gradient(90deg, #00288e, #16A34A)" }} />
        </Box>
      </Modal>

      <Modal
        open={rejectModal}
        onClose={() => setRejectModal(false)}
        slotProps={{ backdrop: { sx: { backdropFilter: "blur(8px)", backgroundColor: "rgba(15, 23, 42, 0.4)" } } }}
      >
        <Box sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 480,
          bgcolor: "background.paper",
          borderRadius: "16px",
          boxShadow: "0 20px 50px rgba(0,0,0,0.2)",
          overflow: "hidden",
          border: "1px solid",
          borderColor: "divider"
        }}>
          <Box sx={{ p: 5, textAlign: "center" }}>
            <Box sx={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              bgcolor: "error.light",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 4
            }}>
              <Cancel sx={{ color: "error.main", fontSize: 48 }} />
            </Box>
            <Typography variant="h5" fontWeight={700} mb={1.5}>
              Reject Withdrawal Request
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={4}>
              Are you sure you want to decline this transaction? This action cannot be undone.
            </Typography>
            <Box sx={{
              bgcolor: "action.hover",
              borderRadius: 2,
              p: 2.5,
              mb: 4,
              border: "1px solid",
              borderColor: "divider",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              textAlign: "left"
            }}>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: "uppercase", fontSize: 10 }}>
                  Member
                </Typography>
                <Typography variant="body2" fontWeight={600} color="primary">
                  {selectedRow?.member_name?.split("(")[0]?.trim() || selectedRow?.member_name}
                </Typography>
              </Box>
              <Box sx={{ textAlign: "right" }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: "uppercase", fontSize: 10 }}>
                  Amount
                </Typography>
                <Typography variant="h6" fontWeight={700} color="error">
                  ₹{selectedRow?.amount}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ textAlign: "left", mb: 1 }}>
              <Typography variant="body2" fontWeight={600} color="text.secondary" mb={1}>
                Reason for rejection <Typography component="span" color="error.main">*</Typography>
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="Please provide a clear reason for the user..."
                value={rejectReason}
                onChange={(e) => {
                  if (e.target.value.length <= 200) setRejectReason(e.target.value);
                }}
                size="small"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px"
                  }
                }}
              />
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  {rejectReason.length} / 200 characters
                </Typography>
              </Box>
            </Box>
          </Box>
          <Stack
            direction="row"
            spacing={2}
            sx={{
              p: 3,
              bgcolor: "action.hover",
              borderTop: "1px solid",
              borderColor: "divider"
            }}
          >
            <Button
              fullWidth
              variant="outlined"
              onClick={() => setRejectModal(false)}
              sx={{ py: 1.5, borderRadius: "12px", textTransform: "none", fontWeight: 600 }}
            >
              Cancel
            </Button>
            <Button
              fullWidth
              variant="contained"
              color="error"
              onClick={handleReject}
              disabled={actionLoading || !rejectReason.trim()}
              startIcon={actionLoading && <CircularProgress size={18} color="inherit" />}
              sx={{ py: 1.5, borderRadius: "12px", textTransform: "none", fontWeight: 600 }}
            >
              Confirm Reject
            </Button>
          </Stack>
        </Box>
      </Modal>

      <Modal
        open={qrModal}
        onClose={() => { setQrModal(false); setQrDataUrl(""); }}
        slotProps={{ backdrop: { sx: { backdropFilter: "blur(8px)", backgroundColor: "rgba(15, 23, 42, 0.4)" } } }}
      >
        <Box sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 400,
          bgcolor: "background.paper",
          borderRadius: "24px",
          boxShadow: "0 20px 50px rgba(0,0,0,0.2)",
          overflow: "hidden",
          border: "1px solid",
          borderColor: "divider"
        }}>
          <Box sx={{ p: 5, textAlign: "center" }}>
            <Typography variant="h5" fontWeight={700} mb={1}>
              UPI QR Code
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Scan to pay <strong>₹{qrRow?.amount}</strong>
            </Typography>
            <Box sx={{
              display: "flex",
              justifyContent: "center",
              mb: 3,
              p: 3,
              bgcolor: "#fff",
              borderRadius: "16px",
              border: "1px solid",
              borderColor: "divider",
              width: "fit-content",
              mx: "auto"
            }}>
              {qrDataUrl ? (
                <Box component="img" src={qrDataUrl} alt="UPI QR Code" sx={{ width: 220, height: 220 }} />
              ) : (
                <CircularProgress size={40} />
              )}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ wordBreak: "break-all" }}>
              UPI ID: {qrRow?.upi_id}
            </Typography>
          </Box>
          <Box sx={{ p: 2, textAlign: "center", borderTop: "1px solid", borderColor: "divider" }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => { setQrModal(false); setQrDataUrl(""); }}
              sx={{ py: 1.2, borderRadius: "12px", textTransform: "none", fontWeight: 600 }}
            >
              Close
            </Button>
          </Box>
        </Box>
      </Modal>

      <Modal
        open={screenshotModal}
        onClose={() => { setScreenshotModal(false); setScreenshotViewUrl(""); setZoom(1); setPan({ x: 0, y: 0 }); }}
        slotProps={{ backdrop: { sx: { backdropFilter: "blur(8px)", backgroundColor: "rgba(15, 23, 42, 0.4)" } } }}
      >
        <Box sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: 700,
          maxWidth: "92vw",
          maxHeight: "90vh",
          bgcolor: "background.paper",
          borderRadius: "24px",
          boxShadow: "0 20px 50px rgba(0,0,0,0.2)",
          overflow: "hidden",
          border: "1px solid",
          borderColor: "divider",
          display: "flex",
          flexDirection: "column"
        }}>
          <Box sx={{ p: 3, textAlign: "center", borderBottom: "1px solid", borderColor: "divider" }}>
            <Typography variant="h6" fontWeight={700} mb={1}>
              Payment Screenshot
            </Typography>
            <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
              <Tooltip title="Zoom Out">
                <IconButton
                  size="small"
                  onClick={() => setZoom((z) => Math.max(0.25, z - 0.25))}
                  disabled={zoom <= 0.25}
                  sx={{ border: "1px solid", borderColor: "divider" }}
                >
                  <ZoomOut fontSize="small" />
                </IconButton>
              </Tooltip>
              <Typography variant="body2" fontWeight={600} sx={{ minWidth: 50, textAlign: "center" }}>
                {Math.round(zoom * 100)}%
              </Typography>
              <Tooltip title="Zoom In">
                <IconButton
                  size="small"
                  onClick={() => setZoom((z) => Math.min(4, z + 0.25))}
                  disabled={zoom >= 4}
                  sx={{ border: "1px solid", borderColor: "divider" }}
                >
                  <ZoomIn fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Reset All">
                <IconButton
                  size="small"
                  onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }}
                  disabled={zoom === 1 && pan.x === 0 && pan.y === 0}
                  sx={{ border: "1px solid", borderColor: "divider" }}
                >
                  <RestartAlt fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Box>
          <Box
            sx={{
              flex: 1,
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 2,
              minHeight: 300,
              cursor: zoom > 1 ? (dragRef.current.active ? "grabbing" : "grab") : "default"
            }}
            onWheel={(e) => {
              e.preventDefault();
              setZoom((z) => {
                const delta = e.deltaY > 0 ? -0.1 : 0.1;
                return Math.min(4, Math.max(0.25, +(z + delta).toFixed(2)));
              });
            }}
            onMouseDown={(e) => {
              if (zoom <= 1) return;
              e.preventDefault();
              dragRef.current = { active: true, startX: e.clientX, startY: e.clientY, panX: pan.x, panY: pan.y };
            }}
            onMouseMove={(e) => {
              if (!dragRef.current.active) return;
              const dx = e.clientX - dragRef.current.startX;
              const dy = e.clientY - dragRef.current.startY;
              setPan({ x: dragRef.current.panX + dx, y: dragRef.current.panY + dy });
            }}
            onMouseUp={() => { dragRef.current.active = false; }}
            onMouseLeave={() => { dragRef.current.active = false; }}
          >
            {screenshotViewUrl ? (
              <Box
                component="img"
                src={screenshotViewUrl}
                alt="Payment Screenshot"
                draggable={false}
                sx={{
                  width: "100%",
                  maxHeight: 500,
                  objectFit: "contain",
                  borderRadius: "12px",
                  border: "1px solid",
                  borderColor: "divider",
                  transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                  transformOrigin: "center center",
                  transition: dragRef.current.active ? "none" : "transform 0.15s ease",
                  userSelect: "none"
                }}
              />
            ) : (
              <Typography variant="body2" color="text.secondary">
                No screenshot available
              </Typography>
            )}
          </Box>
          <Box sx={{ p: 2, textAlign: "center", borderTop: "1px solid", borderColor: "divider" }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => { setScreenshotModal(false); setScreenshotViewUrl(""); setZoom(1); setPan({ x: 0, y: 0 }); }}
              sx={{ py: 1.2, borderRadius: "12px", textTransform: "none", fontWeight: 600 }}
            >
              Close
            </Button>
          </Box>
        </Box>
      </Modal>
    </Page>
  );
}
